import React, { useMemo, useRef, useState } from 'react';
import { Box, List, ListItem, ListItemButton, Sheet, Textarea, Typography } from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import { AdminStaffAPI } from '../../lib/apiAdmin';
import type { AssignableStaff } from '../../types/admin';

/** The handle the backend matches on — the address's local part. */
const handleFor = (person: AssignableStaff) =>
  (person.email || '').split('@')[0] || String(person.id);

/**
 * A comment box that can address somebody.
 *
 * Typing `@` opens a list of staff; picking one inserts their handle, which the
 * server resolves and turns into a notification. The list is staff only, which
 * mirrors the server rule — a customer's name must never become a message sent
 * to them.
 *
 * The picker is a convenience, not the mechanism. Someone who types `@dana`
 * without opening it gets exactly the same result, so this cannot become the
 * thing that has to work for mentions to work.
 */
export const MentionTextarea: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
  disabled?: boolean;
}> = ({ value, onChange, placeholder, minRows = 3, disabled }) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [anchor, setAnchor] = useState(0);

  const { data: staff = [] } = useQuery({
    queryKey: ['admin', 'assignableStaff'],
    queryFn: () => AdminStaffAPI.assignable(),
    staleTime: 5 * 60 * 1000,
  });

  const matches = useMemo(() => {
    if (query === null) return [];
    const needle = query.toLowerCase();
    return staff
      .filter(
        (person) =>
          !needle ||
          person.name.toLowerCase().includes(needle) ||
          handleFor(person).toLowerCase().startsWith(needle)
      )
      .slice(0, 6);
  }, [staff, query]);

  /** Read the partial handle immediately behind the caret, if we are in one. */
  const syncQuery = (text: string, caret: number) => {
    const upto = text.slice(0, caret);
    const at = upto.lastIndexOf('@');
    if (at === -1) return setQuery(null);

    // Only a fresh word counts, so an email address in prose does not open the
    // list — the same boundary the server's pattern uses.
    const before = at === 0 ? '' : upto[at - 1];
    if (before && !/\s|[([{]/.test(before)) return setQuery(null);

    const partial = upto.slice(at + 1);
    if (/\s/.test(partial)) return setQuery(null);

    setAnchor(at);
    setQuery(partial);
  };

  const insert = (person: AssignableStaff) => {
    const caret = ref.current?.selectionStart ?? value.length;
    const next = `${value.slice(0, anchor)}@${handleFor(person)} ${value.slice(caret)}`;
    onChange(next);
    setQuery(null);
    requestAnimationFrame(() => ref.current?.focus());
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Textarea
        slotProps={{ textarea: { ref } }}
        size="sm"
        minRows={minRows}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value);
          syncQuery(event.target.value, event.target.selectionStart ?? 0);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && query !== null) {
            event.stopPropagation();
            setQuery(null);
          }
        }}
        onBlur={() => {
          // Deferred so a click on the list lands before it closes.
          window.setTimeout(() => setQuery(null), 150);
        }}
      />

      {query !== null && matches.length > 0 && (
        <Sheet
          variant="outlined"
          sx={{
            position: 'absolute',
            zIndex: 10,
            left: 0,
            right: 0,
            mt: 0.5,
            borderRadius: 'sm',
            boxShadow: 'sm',
            maxHeight: 220,
            overflowY: 'auto',
          }}
        >
          <List size="sm">
            {matches.map((person) => (
              <ListItem key={person.id}>
                <ListItemButton onMouseDown={(event) => event.preventDefault()} onClick={() => insert(person)}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-sm" noWrap>
                      {person.name}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
                      @{handleFor(person)}
                    </Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Sheet>
      )}
    </Box>
  );
};

export default MentionTextarea;
